import { createSlice } from '@reduxjs/toolkit'

const dashboardStatus = Object.freeze({
    LOADING: 'LOADING',
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE',
})

const initialState = {
    status: {
        recommended: dashboardStatus.LOADING,
        courses: dashboardStatus.LOADING,
        classes: dashboardStatus.LOADING,
        userCourses: dashboardStatus.LOADING,
        businessUserStatistics: dashboardStatus.LOADING,
        completionRate: dashboardStatus.LOADING,
        leaderboard: dashboardStatus.LOADING,
        businessUserCourses: dashboardStatus.LOADING,
        courseBeneficiaries: dashboardStatus.LOADING,
    },
    errorMessage: {
        recommended: null,
        courses: null,
        classes: null,
        userCourses: null,
        businessUserStatistics: null,
        completionRate: null,
        leaderboard: null,
        businessUserCourses: null,
        courseBeneficiaries: null,
    },
    recommended: [],
    courses: [],
    classes: [],
    userCourses: [],
    businessUserStatistics: {},
    completionRate: [],
    leaderboard: [],
    businessUserCourses: [],
    courseBeneficiaries: [],
}

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        getRecommendedCourses: state => {
            state.status.recommended = dashboardStatus.LOADING
        },
        getRecommendedCoursesSuccess: (state, action) => {
            state.recommended = action.payload
            state.status.recommended = dashboardStatus.SUCCESS
        },
        getRecommendedCoursesError: (state, action) => {
            state.errorMessage.recommended = action.payload
            state.status.recommended = dashboardStatus.FAILURE
        },
        getOurCourses: state => {
            state.status.courses = dashboardStatus.LOADING
        },
        getOurCoursesSuccess: (state, action) => {
            state.courses = action.payload
            state.status.courses = dashboardStatus.SUCCESS
        },
        getOurCoursesError: (state, action) => {
            state.errorMessage.courses = action.payload
            state.status.courses = dashboardStatus.FAILURE
        },
        getUpcomingClasses: state => {
            state.status.classes = dashboardStatus.LOADING
        },
        getUpcomingClassesSuccess: (state, action) => {
            state.classes = action.payload
            state.status.classes = dashboardStatus.SUCCESS
        },
        getUpcomingClassesFailure: (state, action) => {
            state.errorMessage.classes = action.payload
            state.status.classes = dashboardStatus.FAILURE
        },
        getUserCourses: state => {
            state.status.userCourses = dashboardStatus.LOADING
        },
        getUserCoursesSuccess: (state, action) => {
            state.userCourses = action.payload
            state.status.userCourses = dashboardStatus.SUCCESS
        },
        getUserCoursesError: (state, action) => {
            state.errorMessage.userCourses = action.payload
            state.status.userCourses = dashboardStatus.FAILURE
        },
        getBusinessUserStatistics: state => {
            state.status.businessUserStatistics = dashboardStatus.LOADING
            state.errorMessage.businessUserStatistics = null
        },
        getBusinessUserStatisticsSuccess: (state, action) => {
            state.businessUserStatistics = action.payload
            state.status.businessUserStatistics = dashboardStatus.SUCCESS
        },
        getBusinessUserStatisticsError: (state, action) => {
            state.errorMessage.businessUserStatistics = action.payload
            state.status.businessUserStatistics = dashboardStatus.FAILURE
        },
        getCompletionRate: state => {
            state.status.completionRate = dashboardStatus.LOADING
            state.errorMessage.completionRate = null
        },
        getCompletionRateSuccess: (state, action) => {
            state.completionRate = action.payload
            state.status.completionRate = dashboardStatus.SUCCESS
        },
        getCompletionRateError: (state, action) => {
            state.errorMessage.completionRate = action.payload
            state.status.completionRate = dashboardStatus.FAILURE
        },
        getLeaderboard: state => {
            state.status.leaderboard = dashboardStatus.LOADING
            state.errorMessage.leaderboard = null
        },
        getLeaderboardSuccess: (state, action) => {
            state.leaderboard = action.payload
            state.status.leaderboard = dashboardStatus.SUCCESS
        },
        getLeaderboardError: (state, action) => {
            state.errorMessage.leaderboard = action.payload
            state.status.leaderboard = dashboardStatus.FAILURE
        },
        getBusinessUserCourses: state => {
            state.status.businessUserCourses = dashboardStatus.LOADING
            state.errorMessage.businessUserCourses = null
        },
        getBusinessUserCoursesSuccess: (state, action) => {
            state.businessUserCourses = action.payload
            state.status.businessUserCourses = dashboardStatus.SUCCESS
        },
        getBusinessUserCoursesError: (state, action) => {
            state.errorMessage.businessUserCourses = action.payload
            state.status.businessUserCourses = dashboardStatus.FAILURE
        },
        getCourseBeneficiaries: state => {
            state.status.courseBeneficiaries = dashboardStatus.LOADING
            state.errorMessage.courseBeneficiaries = null
        },
        getCourseBeneficiariesSuccess: (state, action) => {
            state.courseBeneficiaries = action.payload
            state.status.courseBeneficiaries = dashboardStatus.SUCCESS
        },
        getCourseBeneficiariesError: (state, action) => {
            state.errorMessage.courseBeneficiaries = action.payload
            state.status.courseBeneficiaries = dashboardStatus.FAILURE
        },
    },
})

const {
    getRecommendedCourses,
    getRecommendedCoursesSuccess,
    getRecommendedCoursesError,
    getOurCourses,
    getOurCoursesSuccess,
    getOurCoursesError,
    getUpcomingClasses,
    getUpcomingClassesSuccess,
    getUpcomingClassesFailure,
    getUserCourses,
    getUserCoursesSuccess,
    getUserCoursesError,
    getBusinessUserStatistics,
    getBusinessUserStatisticsSuccess,
    getBusinessUserStatisticsError,
    getCompletionRate,
    getCompletionRateSuccess,
    getCompletionRateError,
    getLeaderboard,
    getLeaderboardSuccess,
    getLeaderboardError,
    getBusinessUserCourses,
    getBusinessUserCoursesSuccess,
    getBusinessUserCoursesError,
    getCourseBeneficiaries,
    getCourseBeneficiariesSuccess,
    getCourseBeneficiariesError,
} = dashboardSlice.actions

export const dashboardActions = {
    getRecommendedCourses,
    getRecommendedCoursesSuccess,
    getRecommendedCoursesError,
    getOurCourses,
    getOurCoursesSuccess,
    getOurCoursesError,
    getUpcomingClasses,
    getUpcomingClassesSuccess,
    getUpcomingClassesFailure,
    getUserCourses,
    getUserCoursesSuccess,
    getUserCoursesError,
    getBusinessUserStatistics,
    getBusinessUserStatisticsSuccess,
    getBusinessUserStatisticsError,
    getCompletionRate,
    getCompletionRateSuccess,
    getCompletionRateError,
    getLeaderboard,
    getLeaderboardSuccess,
    getLeaderboardError,
    getBusinessUserCourses,
    getBusinessUserCoursesSuccess,
    getBusinessUserCoursesError,
    getCourseBeneficiaries,
    getCourseBeneficiariesSuccess,
    getCourseBeneficiariesError,
}

export const dashboardSelectors = {
    errorMessage: state => {
        return {
            recommended: state.dashboard.errorMessage.recommended,
            courses: state.dashboard.errorMessage.courses,
            classes: state.dashboard.errorMessage.classes,
            userCourses: state.dashboard.errorMessage.userCourses,
            businessUserStatistics:
                state.dashboard.errorMessage.businessUserStatistics,
            completionRate: state.dashboard.errorMessage.completionRate,
            leaderboard: state.dashboard.errorMessage.leaderboard,
            businessUserCourses:
                state.dashboard.errorMessage.businessUserCourses,
            courseBeneficiaries:
                state.dashboard.errorMessage.courseBeneficiaries,
        }
    },
    isFetching: state => {
        return {
            recommended:
                state.dashboard.status.recommended === dashboardStatus.LOADING,
            courses: state.dashboard.status.courses === dashboardStatus.LOADING,
            classes: state.dashboard.status.classes === dashboardStatus.LOADING,
            userCourses:
                state.dashboard.status.userCourses === dashboardStatus.LOADING,
            businessUserStatistics:
                state.dashboard.status.businessUserStatistics ===
                dashboardStatus.LOADING,
            completionRate:
                state.dashboard.status.completionRate ===
                dashboardStatus.LOADING,
            leaderboard:
                state.dashboard.status.leaderboard === dashboardStatus.LOADING,
            businessUserCourses:
                state.dashboard.status.businessUserCourses ===
                dashboardStatus.LOADING,
            courseBeneficiaries:
                state.dashboard.status.courseBeneficiaries ===
                dashboardStatus.LOADING,
        }
    },
    hasError: state => {
        return {
            recommended:
                state.dashboard.status.recommended === dashboardStatus.FAILURE,
            courses: state.dashboard.status.courses === dashboardStatus.FAILURE,
            classes: state.dashboard.status.classes === dashboardStatus.FAILURE,
            userCourses:
                state.dashboard.status.userCourses === dashboardStatus.FAILURE,
            businessUserStatistics:
                state.dashboard.status.businessUserStatistics ===
                dashboardStatus.FAILURE,
            completionRate:
                state.dashboard.status.completionRate ===
                dashboardStatus.FAILURE,
            leaderboard:
                state.dashboard.status.leaderboard === dashboardStatus.FAILURE,
            businessUserCourses:
                state.dashboard.status.businessUserCourses ===
                dashboardStatus.FAILURE,
            courseBeneficiaries:
                state.dashboard.status.courseBeneficiaries ===
                dashboardStatus.FAILURE,
        }
    },
    recommended: state => state.dashboard.recommended,
    courses: state => state.dashboard.courses,
    classes: state => state.dashboard.classes,
    userCourses: state => state.dashboard.userCourses,
    businessUserStatistics: state => state.dashboard.businessUserStatistics,
    completionRate: state => state.dashboard.completionRate,
    leaderboard: state => state.dashboard.leaderboard,
    businessUserCourses: state => state.dashboard.businessUserCourses,
    courseBeneficiaries: state => state.dashboard.courseBeneficiaries,
}

const dashboardReducer = dashboardSlice.reducer

export default dashboardReducer
